/**
 * §41.7 — the `{macro}` placeholders an affiliate may put in a postback URL.
 *
 * Here rather than in the sender because two callers fill the same URL: the
 * delivery worker, with the conversion's own fields, and the test button, with
 * stand-in values. A test that sent the URL with its placeholders still in it
 * was testing a request no real delivery ever makes — a tracker that answers
 * a literal `{click_id}` with 400 made the test report that real deliveries
 * would fail, when they would not.
 *
 * Two amounts, on purpose: `{revenue}` is the commission in minor units
 * (`50000`), which integrations written before `{payout}` existed already
 * parse, and `{payout}` is the same figure in major units at the currency's
 * own precision (`500.00`), which is what a tracker's payout field expects.
 * Both are a commission, so both are empty on the event postbacks
 * (REGISTRATION, FTD, DEPOSIT), which report what the player did whatever the
 * deal pays: those carry `{deposit_amount}` instead, the deposit in major
 * units (`25.00`) beside its `{currency}`, empty on a registration.
 *
 * WITHDRAWAL and WITHDRAWAL_REVERSED carry the withdrawal the same way, in
 * `{withdrawal_amount}`. REVENUE, sent when a period is locked, carries the
 * affiliate's own share of one player's NGR for that period in `{ngr}`
 * (major units, signed: a player who won is negative) and the period's
 * bounds in `{period_start}` and `{period_end}`. Each is empty on every
 * other event.
 *
 * `{brand_url}` (brand profile, 0331) is the web app address of the player's
 * brand, filled when the postback is sent rather than stored in the payload,
 * so it is the brand's address at the time of sending. Empty when the brand
 * has none.
 */
/** Every placeholder a postback URL may carry, in the order the payload lists them. */
export const POSTBACK_MACRO_KEYS = [
    'event', 'id', 'status', 'goal', 'revenue', 'payout', 'currency', 'deposit_amount', 'player_ref',
    'click_id', 'tid', 'advertiser_id', 'partner_id', 'offer_id', 'country',
    'conversion_date',
    'withdrawal_amount', 'ngr', 'period_start', 'period_end',
    'brand_url',
    'sub_id_1', 'sub_id_2', 'sub_id_3', 'sub_id_4', 'sub_id_5',
    'sub_id_6', 'sub_id_7', 'sub_id_8', 'sub_id_9', 'sub_id_10',
];
/**
 * Fills `{macro}` placeholders from `fields`. An unrecognized `{placeholder}`
 * is left untouched rather than stripped, so a typo in the affiliate's own
 * URL fails loudly instead of silently swallowing a literal brace.
 *
 * Also fills the percent-encoded form, `%7Bkey%7D` (any hex case):
 * registrations stored before the URL was kept exactly as entered went
 * through `new URL(raw).toString()`, which WHATWG encodes in the path, so
 * `{click_id}` was written as `%7Bclick_id%7D` and never matched.
 *
 * One pass over both forms, never two. Each value is percent-encoded as it
 * goes in, so a sub-ID that arrived as the text `{revenue}` becomes
 * `%7Brevenue%7D` — and a second pass looking for the encoded form would then
 * fill the revenue into it, putting a figure where the affiliate's own sub-ID
 * belonged.
 */
/** Amount and NGR macros gated by `postbacks.postback_amount_macros_enabled` (F-081). */
export const POSTBACK_AMOUNT_MACRO_KEYS = [
    'deposit_amount',
    'withdrawal_amount',
    'ngr',
];
/** Clears amount/NGR macro values when the brand has not enabled them. */
export function gatePostbackAmountMacros(fields, enabled) {
    if (enabled)
        return fields;
    return {
        ...fields,
        ...(fields.deposit_amount !== undefined ? { deposit_amount: '' } : {}),
        ...(fields.withdrawal_amount !== undefined ? { withdrawal_amount: '' } : {}),
        ...(fields.ngr !== undefined ? { ngr: '' } : {}),
    };
}
export function fillPostbackMacros(url, fields) {
    const map = fields;
    return url.replace(/\{(\w+)\}|%7b(\w+)%7d/gi, (whole, plain, encoded) => {
        const key = plain ?? encoded;
        return Object.prototype.hasOwnProperty.call(map, key) ? encodeURIComponent(map[key]) : whole;
    });
}
/**
 * Stand-in values for a test send: every placeholder filled, each value
 * plainly a test rather than something a tracker could take for a real
 * conversion or a real player.
 */
export function postbackTestFields(now) {
    const fields = Object.fromEntries(POSTBACK_MACRO_KEYS.map((k) => [k, 'test']));
    fields.event = 'TEST';
    fields.revenue = '0';
    // A decimal like every real {payout}: a tracker that parses the field as a
    // number would refuse the word 'test' and fail a test that real deliveries pass.
    fields.payout = '0.00';
    fields.deposit_amount = '0.00';
    fields.withdrawal_amount = '0.00';
    fields.ngr = '0.00';
    fields.conversion_date = now.toISOString();
    // A period is a pair of instants like every real one: the day before the
    // test, to the start of the test's own day.
    const dayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    fields.period_start = new Date(dayStart.getTime() - 86_400_000).toISOString();
    fields.period_end = dayStart.toISOString();
    // An address like every real one. The test send puts the brand's own in
    // its place when it knows which brand that is.
    fields.brand_url = 'https://brand.example/';
    return fields;
}
//# sourceMappingURL=postback-macros.js.map